import type { ReactNode } from 'react';
import {
  ArrowUpRight,
  BookOpen,
  ChevronRight,
  Github,
  HelpCircle,
  Mail,
  MessageCircle,
  Radio,
  Scale,
  Target,
} from 'lucide-react';
import type { BriefSourcePanel, BriefSourceTopic, BriefSourceTopicItem } from '../../hooks/useTeamTodaySurface';
import { useTeamStats } from '../../hooks/useTeamStats';
import { siteLabel } from '../../lib/sites';
import { askPrefillFor } from '../../lib/brief-card-exits';
import type { RdCardConfig } from './RdCardDialog';

/*
 * SourcePanels — the per-source distillation rail (mock-alignment phase 2, Teddy 2026-07-26).
 *
 * The mock's §5 MessagePanel (Email / Slack) anatomy on REAL rails: each panel carries ≤3
 * LLM-grouped topic cards (grouping happens MVP-side at brief-gen time); a topic card shows the
 * count chip, the computed action/reference split, and a one-line preview, and drills into
 * RdCardDialog where every item deep-links to the REAL source (Slack thread permalink / Gmail
 * thread / GitHub PR — §8 traceability). The band/task 3-caps route their overflow here, so
 * nothing readable is ever silently dropped.
 *
 * Static section sizing (Teddy 2026-07-27): Email and Slack ALWAYS render at equal height — an
 * empty panel shows a skeleton fallback instead of collapsing. GitHub moved out of the comms grid
 * into the mock's "Role specific sources" section (github-panel.tsx anatomy): OverviewCard rows
 * beside the GithubStatsBoard, exported as GithubSourcePanel for BriefSurface to place.
 *
 * What deliberately did NOT survive the port from the mock: the group verbs (Open all / Save for
 * later / Snooze / Archive) — no rail exists for any of them. Honest verbs only: the per-item
 * source link + the in-app note nav.
 */

// Per-source only (Teddy 2026-07-27): the mock's buckets are the buckets — the catch-all
// "High-level overview" panel is gone (it duplicated the band's "High-level summary" register).
// Legacy stored briefs may still carry source:"overview" rows; they simply don't render.
const COMMS_META: Record<string, { icon: ReactNode; title: string; subtitle: string; empty: string }> = {
  email: {
    icon: <Mail size={19} />,
    title: 'Email',
    subtitle: 'A concise read of what is waiting',
    empty: "Nothing from email in today's brief.",
  },
  slack: {
    icon: <MessageCircle size={19} />,
    title: 'Slack',
    subtitle: 'A concise read of what is waiting',
    empty: "Nothing from Slack in today's brief.",
  },
};

const GITHUB_META = { icon: <Github size={19} />, title: 'GitHub', subtitle: 'A small read of what moved' };

// Topic icon by dominant tag (the split's first word) — deterministic, no per-topic LLM icon.
function topicIcon(topic: BriefSourceTopic): ReactNode {
  const first = (topic.split ?? '').split(' ')[1] ?? '';
  switch (first) {
    case 'action': return <Target size={16} />;
    case 'question': return <HelpCircle size={16} />;
    case 'decision': return <Scale size={16} />;
    case 'update': return <Radio size={16} />;
    default: return <BookOpen size={16} />;
  }
}

// PR-3: an item whose story object has more than one member arrived in more than one place. The
// row already IS one of those members, so the only new fact is WHERE ELSE it showed up — a tail on
// the existing provenance line ("From Jordan · also in Slack, Email"), not extra rows. The item's
// own citation is excluded by conversationId; sibling site labels are de-duplicated because two
// Slack copies of the same artifact are one place, not two.
function alsoInLine(item: BriefSourceTopicItem): string | null {
  const labels: string[] = [];
  for (const c of item.citations ?? []) {
    if (!c?.site || c.conversationId === item.conversationId) continue;
    const label = siteLabel(c.site);
    if (!labels.includes(label)) labels.push(label);
  }
  return labels.length > 0 ? `also in ${labels.join(', ')}` : null;
}

/*
 * How many item rows a panel drill-in renders before its "+N more" (Teddy, r2: "if cramped, the
 * section for email and slack should be a scrollable card, and they should expand like discussed in
 * the spec"). A GitHub topic shipped 23 rows into a 92vh dialog, which buried the card's own exits
 * and Done below several screens of list.
 *
 * DERIVATION — the same measurement BriefSurface's TAIL_POPOUT_ROW_CAP (= 4) was cut from: a
 * dialog screen holds ≈4 rows of this anatomy, and a panel item row is that anatomy (title + a
 * provenance line + the source/note link row) rather than anything shorter. The other candidate
 * precedent, the MVP's STATS_PR_LIST_CAP = 10, is a PAYLOAD cap on an evidence list — how much the
 * wire carries, not how much fits on a screen — so it is the wrong number to copy here.
 *
 * The cap is per LIST, not per panel, and it lives on the shared card builder, so email, Slack and
 * GitHub get it identically.
 */
const PANEL_ITEM_ROW_CAP = 4;

function topicToCard(
  meta: { icon: ReactNode; title: string },
  topic: BriefSourceTopic,
  onNavNote: (conversationId: string) => void,
  onAsk: (prefill: string) => void,
): RdCardConfig {
  return {
    icon: meta.icon,
    eyebrow: meta.title,
    title: topic.title,
    description: topic.description ?? topic.preview ?? null,
    itemsLabel: 'In this group',
    items: (topic.items ?? []).map(it => ({
      title: it.title,
      note: [it.note ?? null, alsoInLine(it)].filter(Boolean).join(' · ') || null,
      tag: it.tag ?? null,
      href: it.url ?? null,
      conversationId: it.conversationId ?? null,
    })),
    itemsCap: PANEL_ITEM_ROW_CAP,
    actions: [], // honest verbs live on the item rows (source link + note nav)
    // C1: a topic GROUPS several notes, so it has no single note and no single artifact of its own —
    // those stay on the rows, which already carry both. What it gains is the contract's third exit,
    // which needs no ids at all, so a panel drill-in is never a page you can only look at.
    primary: {
      sourceTargets: null,
      externalUrl: null,
      noteConversationId: null,
      askPrefill: askPrefillFor(topic.title),
      onNavNote,
      onAsk,
    },
    onNavNote,
  };
}

function TopicCard({
  source,
  topic,
  onOpen,
}: {
  source: string;
  topic: BriefSourceTopic;
  onOpen: () => void;
}) {
  const count = topic.count ?? topic.items?.length ?? 0;
  return (
    <button
      onClick={onOpen}
      className="group w-full rounded-2xl border border-[#eadfce] bg-[#fbf7ef] p-4 text-left shadow-[0_5px_12px_rgba(88,69,42,.025)] transition hover:-translate-y-0.5 hover:border-acc hover:bg-acc-soft hover:shadow-[0_10px_19px_rgba(88,69,42,.08)] dark:border-border dark:bg-muted"
      data-testid={`sp-topic-${source}`}
    >
      <div className="flex items-start gap-3">
        <span className="grid size-8 shrink-0 place-items-center rounded-lg bg-muted text-[#746954] dark:bg-card dark:text-muted-foreground">
          {topicIcon(topic)}
        </span>
        <span className="min-w-0 flex-1">
          <span className="flex items-center justify-between gap-3">
            {/* min-w-0: without it this flex item refuses to shrink below its one-line width. */}
            <span className="min-w-0 text-[15px] font-medium">{topic.title}</span>
            <span className="shrink-0 rounded-full bg-[#ebe2d4] px-2 py-0.5 font-['DM_Mono'] text-[9px] text-muted-foreground dark:bg-card">
              {count} item{count === 1 ? '' : 's'}
            </span>
          </span>
          {topic.preview && (
            <span className="mt-1 block truncate text-sm leading-5 text-muted-foreground">{topic.preview}</span>
          )}
          <span className="mt-3 flex items-center justify-between">
            <span className="text-xs font-medium text-acc">{topic.split ?? ''}</span>
            <span className="flex items-center gap-1 text-xs text-muted-foreground group-hover:text-acc">
              See the items <ChevronRight size={14} />
            </span>
          </span>
        </span>
      </div>
    </button>
  );
}

/** Ghost topic row — the empty-state skeleton (Teddy 2026-07-27: never drop the section). */
function GhostTopicRow() {
  return (
    <div className="rounded-2xl border border-dashed border-[#eadfce] bg-[#fbf7ef]/60 p-4 dark:border-border dark:bg-muted/40">
      <div className="flex items-start gap-3">
        <span className="size-8 shrink-0 rounded-lg bg-muted/70 dark:bg-card/70" />
        <span className="min-w-0 flex-1 space-y-2 pt-1">
          <span className="block h-3 w-2/3 rounded bg-muted/70 dark:bg-card/70" />
          <span className="block h-3 w-1/3 rounded bg-muted/50 dark:bg-card/50" />
        </span>
      </div>
    </div>
  );
}

function PanelShell({
  source,
  meta,
  children,
}: {
  source: string;
  meta: { icon: ReactNode; title: string; subtitle: string };
  children: ReactNode;
}) {
  return (
    <section
      className="h-full rounded-[24px] border border-border bg-card p-5 shadow-[0_12px_28px_rgba(88,69,42,.04)] sm:p-6"
      data-testid={`sp-panel-${source}`}
    >
      <div className="flex items-start gap-3">
        <span className="grid size-10 place-items-center rounded-xl bg-muted text-[#766b59] shadow-sm dark:text-muted-foreground">
          {meta.icon}
        </span>
        <div>
          <h2 className="text-[19px] font-semibold leading-snug tracking-[-.01em]">{meta.title}</h2>
          <p className="mt-1 text-xs text-muted-foreground">{meta.subtitle}</p>
        </div>
      </div>
      {/* bug-720: grid-cols-1, never bare `grid` — an implicit auto track is floored by the
        * widest child's min-content (a truncate line = the FULL untruncated line), which forced
        * this list past the viewport and clipped every row's right edge. minmax(0,1fr) lets rows
        * shrink so truncate actually ellipsizes. Same fix on every list grid in this sweep. */}
      <div className="mt-5 grid grid-cols-1 gap-2.5">{children}</div>
    </section>
  );
}

/** Email + Slack — always both, equal height; an empty panel keeps its place with a skeleton. */
export function SourcePanels({
  panels,
  onOpenCard,
  onOpenTopic,
  onNavNote,
  onAsk,
}: {
  panels: BriefSourcePanel[];
  onOpenCard: (config: RdCardConfig) => void;
  /** Spec §12: the stored-body ruled branch — returns true when it handled the open (a
   *  pregenerated `panel:<source>:<topicId>` body exists); false falls through to the
   *  legacy item-rows dialog, byte-identical to before. */
  onOpenTopic?: (source: string, topic: BriefSourceTopic) => boolean;
  onNavNote: (conversationId: string) => void;
  onAsk: (prefill: string) => void;
}) {
  return (
    <section className="mt-5 grid grid-cols-1 items-stretch gap-5 lg:grid-cols-2" id="band-sources">
      {(['email', 'slack'] as const).map(source => {
        const meta = COMMS_META[source];
        const topics = panels.find(p => p.source === source)?.topics ?? [];
        return (
          <PanelShell key={source} source={source} meta={meta}>
            {topics.length > 0 ? (
              topics.map(topic => (
                <TopicCard
                  key={topic.id}
                  source={source}
                  topic={topic}
                  onOpen={() => {
                    if (onOpenTopic?.(source, topic)) return;
                    onOpenCard(topicToCard(meta, topic, onNavNote, onAsk));
                  }}
                />
              ))
            ) : (
              <>
                <p className="px-1 text-sm text-muted-foreground">{meta.empty}</p>
                <GhostTopicRow />
                <GhostTopicRow />
              </>
            )}
          </PanelShell>
        );
      })}
    </section>
  );
}

/** GitHub in the mock's OverviewCard register (github-panel.tsx) — lives in the Role-specific-
 * sources section beside the GithubStatsBoard; same drill-in card as the comms topics. */
export function GithubSourcePanel({
  panels,
  onOpenCard,
  onOpenTopic,
  onNavNote,
  onAsk,
  frozen = false,
}: {
  panels: BriefSourcePanel[];
  onOpenCard: (config: RdCardConfig) => void;
  /** Spec §12 — same stored-body ruled branch as SourcePanels. */
  onOpenTopic?: (source: string, topic: BriefSourceTopic) => boolean;
  onNavNote: (conversationId: string) => void;
  onAsk: (prefill: string) => void;
  frozen?: boolean;
}) {
  const topics = panels.find(p => p.source === 'github')?.topics ?? [];
  // Empty-state honesty (review finding 2026-07-27): an empty panel cannot distinguish "watched
  // and quiet" from "never connected" — so the default line claims only what the BRIEF contains,
  // and the live panel upgrades to the board's "Nothing wired" register when the stats rail
  // (same query key as GithubStatsBoard — deduped, no extra fetch) says GitHub isn't connected.
  const { data: stats } = useTeamStats(!frozen);
  const connected = stats?.status === 'ready' ? (stats.roleStats?.github?.connected ?? null) : null;
  const emptyLine = frozen
    ? 'Nothing from GitHub in this brief.'
    : connected === false
      ? 'Nothing wired — connect GitHub to see what moved.'
      : "Nothing from GitHub in today's brief.";
  return (
    <section
      className="h-full rounded-[24px] border border-border bg-card p-5 shadow-[0_16px_34px_rgba(88,69,42,.05)] sm:p-6"
      data-testid="sp-panel-github"
    >
      <div className="flex items-center gap-3">
        <span className="grid size-10 place-items-center rounded-xl bg-[#24292f] text-white shadow-[0_5px_10px_rgba(0,0,0,.14)]">
          {GITHUB_META.icon}
        </span>
        <div>
          <h3 className="font-semibold">{GITHUB_META.title}</h3>
          <p className="mt-0.5 text-xs text-muted-foreground">{GITHUB_META.subtitle}</p>
        </div>
      </div>
      <div className="mt-5 space-y-3">
        {topics.length > 0 ? (
          topics.map((topic, i) => {
            const count = topic.count ?? topic.items?.length ?? 0;
            const chip = topic.split?.split(' · ')[0] ?? `${count} item${count === 1 ? '' : 's'}`;
            const tone = i % 2 === 0 ? 'bg-[#e8e2f0] text-[#4a3d6b]' : 'bg-[#f1e3ce] text-[#8b6546]';
            return (
              <button
                key={topic.id}
                onClick={() => {
                  if (onOpenTopic?.('github', topic)) return;
                  onOpenCard(topicToCard(GITHUB_META, topic, onNavNote, onAsk));
                }}
                className="group w-full rounded-2xl border border-[#ded5c6] bg-[#fbf8f2] p-4 text-left shadow-[0_6px_14px_rgba(88,69,42,.04)] transition hover:-translate-y-0.5 hover:shadow-md dark:border-border dark:bg-muted"
                data-testid="sp-topic-github"
              >
                <span className="flex items-start gap-3">
                  <span className={`grid size-9 shrink-0 place-items-center rounded-xl ${tone}`}>
                    {topicIcon(topic)}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="flex items-start justify-between gap-3">
                      <span className="text-[17px] font-semibold leading-5">{topic.title}</span>
                      <span className={`shrink-0 rounded-full px-2 py-1 font-['DM_Mono'] text-[10px] ${tone}`}>
                        {chip}
                      </span>
                    </span>
                    {topic.preview && (
                      <span className="mt-2 block text-sm leading-5 text-muted-foreground">{topic.preview}</span>
                    )}
                    <span className="mt-4 flex items-center gap-1 text-xs font-medium text-acc">
                      Open the specific work <ArrowUpRight size={13} />
                    </span>
                  </span>
                </span>
              </button>
            );
          })
        ) : (
          <>
            <p className="px-1 text-sm text-muted-foreground">{emptyLine}</p>
            <GhostTopicRow />
            <GhostTopicRow />
          </>
        )}
      </div>
    </section>
  );
}
